import {
  AppState,
  Plan,
  LowIncomeSubsidyStatus,
  SearchResultPlan,
  PlanCategoryType,
} from "../@types";
import { $$ } from "../helpers";
import { CONSTANTS as C } from "./CONSTANTS";

const {
  LIS_LEVEL_1A,
  LIS_LEVEL_1B,
  LIS_LEVEL_1C,
  LIS_LEVEL_2,
  LIS_LEVEL_3,
  LIS_LEVEL_4_100,
  LIS_LEVEL_4_25,
  LIS_LEVEL_4_50,
  LIS_LEVEL_4_75,
  LIS_LEVEL_4_100H,
  LIS_NO_HELP,
} = LowIncomeSubsidyStatus;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type HelperFunc = (
  state: AppState,
  currentPlanYear: number,
  plan: Plan | SearchResultPlan
) => any;

const getLisToUse = (
  { lis = LIS_NO_HELP, futureLis }: AppState,
  mctCurrentPlanYear: number,
  plan: Plan | SearchResultPlan
): LowIncomeSubsidyStatus => {
  // if futureLis is undefined, it means that it is an anonymous user
  // where we use lis for both years
  return mctCurrentPlanYear.toString() === plan.contract_year
    ? lis
    : futureLis
    ? futureLis
    : lis;
};

const getPartDPremiumOrLIS: HelperFunc = (
  state,
  mctCurrentPlanYear = 2021,
  plan
) => {
  if (!plan.lis) {
    return plan.partd_premium;
  }

  const lisToUse = getLisToUse(state, mctCurrentPlanYear, plan);

  if (
    [
      LIS_LEVEL_1A,
      LIS_LEVEL_1B,
      LIS_LEVEL_1C,
      LIS_LEVEL_2,
      LIS_LEVEL_3,
      LIS_LEVEL_4_100,
      LIS_LEVEL_4_100H,
    ].includes(lisToUse)
  ) {
    return plan.lis.level_100;
  } else if ([LIS_LEVEL_4_75].includes(lisToUse)) {
    return plan.lis.level_75;
  } else if ([LIS_LEVEL_4_50].includes(lisToUse)) {
    return plan.lis.level_50;
  } else if ([LIS_LEVEL_4_25].includes(lisToUse)) {
    return plan.lis.level_25;
  } else {
    return plan.partd_premium;
  }
};

const getAnnualDrugDeductible: HelperFunc = (
  state,
  mctCurrentPlanYear = 2021,
  plan
) => {
  const lisToUse = getLisToUse(state, mctCurrentPlanYear, plan);
  if (
    [
      LIS_LEVEL_1A,
      LIS_LEVEL_1B,
      LIS_LEVEL_1C,
      LIS_LEVEL_2,
      LIS_LEVEL_3,
    ].includes(lisToUse)
  ) {
    return "$0.00";
  } else if (
    lisToUse &&
    [
      LIS_LEVEL_4_100,
      LIS_LEVEL_4_25,
      LIS_LEVEL_4_50,
      LIS_LEVEL_4_75,
      LIS_LEVEL_4_100H,
    ].includes(lisToUse)
  ) {
    const maxDrugDeductible =
      C.maxLisDrugDeductibles[plan.contract_year] || plan.drug_plan_deductible;

    return plan.drug_plan_deductible < maxDrugDeductible
      ? $$(plan.drug_plan_deductible)
      : $$(maxDrugDeductible);
  } else {
    return $$(plan.drug_plan_deductible);
  }
};

const getCoinsuranceAmountPreferred: HelperFunc = (
  state,
  mctCurrentPlanYear = 2021,
  plan
) => {
  const lisToUse = getLisToUse(state, mctCurrentPlanYear, plan);
  if (
    [LIS_LEVEL_4_100, LIS_LEVEL_4_25, LIS_LEVEL_4_50, LIS_LEVEL_4_75].includes(
      lisToUse
    )
  ) {
    return "15%";
  } else {
    return plan.coinsurance_amount_preferred;
  }
};

const getPartBPremiumForYear = (year?: string): number =>
  (year && C.partBPremium[year]) ||
  C.partBPremium[Object.keys(C.partBPremium).pop() || ""];

const getPartBPremium = (plan: Plan | SearchResultPlan): number => {
  const premium = C.partBPremium[plan.contract_year];

  return plan.category === PlanCategoryType.MEDICARE_MEDICAID_PLAN
    ? 0
    : premium;
};

export const getLowIncomeSubsidyStatusLabel = (
  year: string,
  level: LowIncomeSubsidyStatus
): string => C.lowIncomeSubsidyStatusLabels[year][level];

const showMMPBenefits = (plan: Plan): boolean | null => {
  return (
    plan.additional_supplemental_benefits &&
    !!plan.additional_supplemental_benefits.mmp_benefits.length &&
    plan.category === PlanCategoryType.MEDICARE_MEDICAID_PLAN
  );
};

export default {
  getAnnualDrugDeductible,
  getCoinsuranceAmountPreferred,
  getLowIncomeSubsidyStatusLabel,
  getPartBPremium,
  getPartBPremiumForYear,
  getPartDPremiumOrLIS,
  showMMPBenefits,
};
